import { SymbolImportError, SYMBOL_LIMITS, validateSymbolDimensions } from './model';

const signature = [137, 80, 78, 71, 13, 10, 26, 10];
const crcTable = Uint32Array.from({ length: 256 }, (_, n) => {
  let c = n;
  for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  return c >>> 0;
});
export function pngSignature(bytes: Uint8Array): boolean {
  return signature.every((value, index) => bytes[index] === value);
}
/** Bounded chunk/CRC/header validation happens before native decoder allocation. */
export function inspectPng(bytes: Uint8Array): { width: number; height: number } {
  const invalid = () => {
    throw new SymbolImportError('This PNG is damaged or has an invalid structure.');
  };
  if (!pngSignature(bytes) || bytes.length > SYMBOL_LIMITS.pngBytes || bytes.length < 45) invalid();
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8,
    width = 0,
    height = 0,
    chunks = 0,
    data = false,
    endedData = false,
    palette = false;
  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset);
    if (++chunks > 4096 || length > bytes.length - offset - 12) invalid();
    const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
    if (!/^[A-Za-z]{4}$/.test(type)) invalid();
    let crc = 0xffffffff;
    for (let i = offset + 4; i < offset + 8 + length; i++)
      crc = crcTable[(crc ^ bytes[i]!) & 255]! ^ (crc >>> 8);
    if ((crc ^ 0xffffffff) >>> 0 !== view.getUint32(offset + 8 + length)) invalid();
    if (['acTL', 'fcTL', 'fdAT'].includes(type))
      throw new SymbolImportError('Animated PNG symbols are not supported. Export a static PNG.');
    if (chunks === 1 && type !== 'IHDR') invalid();
    if (type === 'IHDR') {
      if (chunks !== 1 || length !== 13) invalid();
      width = view.getUint32(offset + 8);
      height = view.getUint32(offset + 12);
      validateSymbolDimensions(width, height);
      if (
        width > SYMBOL_LIMITS.dimension ||
        height > SYMBOL_LIMITS.dimension ||
        width * height > SYMBOL_LIMITS.pixels
      )
        throw new SymbolImportError(
          'This PNG is too large. Use at most 4,096px per side and 16 megapixels.',
        );
      const depth = bytes[offset + 16]!,
        colour = bytes[offset + 17]!;
      const depths: Readonly<Record<number, readonly number[]>> = {
        0: [1, 2, 4, 8, 16],
        2: [8, 16],
        3: [1, 2, 4, 8],
        4: [8, 16],
        6: [8, 16],
      };
      if (
        !depths[colour]?.includes(depth) ||
        bytes[offset + 18] !== 0 ||
        bytes[offset + 19] !== 0 ||
        bytes[offset + 20]! > 1
      )
        invalid();
    } else if (type === 'PLTE') {
      if (palette || data || !length || length > 768 || length % 3) invalid();
      palette = true;
    } else if (type === 'IDAT') {
      if (endedData || (bytes[25] === 3 && !palette)) invalid();
      data = true;
    } else if (type === 'IEND') {
      if (length || !data || offset + 12 !== bytes.length) invalid();
      return { width, height };
    } else {
      if (type[0] === type[0]!.toUpperCase()) invalid();
      if (data) endedData = true;
    }
    offset += 12 + length;
  }
  return invalid();
}
